import type { Id } from "@repo/convex/dataModel";
import { formatUserIdentity, getInitials, UNRESOLVED_USER_LABEL } from "@repo/shared";
import type { UserDisplayInfo } from "@/hooks";

type ClarificationMessageSource = "app" | "teams" | "claudia";

type CurrentUserDisplayInfo = {
  _id: Id<"users">;
  firstName?: string;
  lastName?: string;
  email?: string;
} | null;

export type ClarificationAuthorDisplay = {
  label: string;
  initials: string;
};

/** An author this thread cannot place: no user row, and not one of the machine senders. */
const UNRESOLVED_AUTHOR: ClarificationAuthorDisplay = {
  label: UNRESOLVED_USER_LABEL,
  initials: getInitials(null),
};

export function getCurrentUserClarificationDisplay(
  currentUser: CurrentUserDisplayInfo | undefined
): ClarificationAuthorDisplay {
  if (!currentUser) return UNRESOLVED_AUTHOR;
  const { displayName, initials } = formatUserIdentity(currentUser);
  return displayName ? { label: displayName, initials } : UNRESOLVED_AUTHOR;
}

export function resolveClarificationAuthorDisplay({
  createdBy,
  currentUser,
  userMap,
  source,
}: {
  createdBy?: Id<"users"> | string | null;
  currentUser?: CurrentUserDisplayInfo;
  userMap: Map<string, UserDisplayInfo> | null;
  source?: ClarificationMessageSource;
}): ClarificationAuthorDisplay {
  if (source === "claudia") {
    return { label: "Claudia", initials: "CL" };
  }

  if (createdBy && currentUser?._id === createdBy) {
    return getCurrentUserClarificationDisplay(currentUser);
  }

  if (createdBy) {
    const user = userMap?.get(createdBy);
    if (user) {
      return { label: user.fullName, initials: user.initials };
    }
  }

  if (source === "app" && currentUser) {
    return getCurrentUserClarificationDisplay(currentUser);
  }

  if (source === "teams") {
    return { label: "Teams", initials: "TE" };
  }

  return UNRESOLVED_AUTHOR;
}
